import type { ToolCall } from '@kanbu/schema';
import {
  type FetchUrlArgs,
  type DocumentSearchArgs,
  type WebSearchArgs,
  ToolNames,
} from '@kanbu/schema/contracts';
import { Search, Link2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { DetailChip, DetailLinkChip, DetailRow } from './detail-primitives';

/**
 * Built-in detail for the retrieval tools that have no dedicated shop
 * renderer. Renders the customer-safe facets of the call — the search query
 * or the fetched URL — never raw arguments or internal result payloads.
 * Returns `null` for anything it does not recognize so the trail simply shows
 * the step's label line.
 */
export function BuiltinToolDetail({
  toolCall,
}: {
  toolCall: ToolCall;
}): ReactNode {
  switch (toolCall.name) {
    case ToolNames.DocumentSearch:
    case ToolNames.WebSearch: {
      const query = (toolCall.args as DocumentSearchArgs | WebSearchArgs)
        ?.searchQuery;

      if (!query?.trim()) {
        return null;
      }

      return (
        <DetailRow>
          <DetailChip icon={<Search />}>{query.trim()}</DetailChip>
        </DetailRow>
      );
    }

    case ToolNames.FetchUrl: {
      const url = (toolCall.args as FetchUrlArgs)?.url;

      if (!url?.trim()) {
        return null;
      }

      return (
        <DetailRow>
          <DetailLinkChip href={url} icon={<Link2 />}>
            {url}
          </DetailLinkChip>
        </DetailRow>
      );
    }

    default:
      return null;
  }
}
